import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  IndexTable,
  InlineStack,
  Page,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import type { SessionStatus, SigningSession } from '../types';

function statusTone(s: SessionStatus): BadgeProps['tone'] {
  const map: Partial<Record<SessionStatus, BadgeProps['tone']>> = {
    PENDING: 'attention',
    VIEWED: 'info',
    SIGNED: 'success',
    DECLINED: 'critical',
    EXPIRED: 'warning',
  };
  return map[s];
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const PAGE_SIZE = 50;

/** An active template that can be sent without an order. */
type SendOption = {
  id: string;
  name: string;
  signers: { position: number; role: string; name: string }[];
  available: boolean;
};

export default function SessionsPage() {
  const { request } = useApi();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SigningSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  // What's typed in the search box, and the search actually applied (after a short pause).
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A new filter or search starts again from the first page.
  useEffect(() => { setPage(0); }, [statusFilter, search]);

  // Manual send search
  const [orderQuery, setOrderQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleManualSearch = useCallback(async () => {
    if (!orderQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await request<{ found: boolean; order: { id: string; orderNumber: string } | null }>(
        `/orders/search?q=${encodeURIComponent(orderQuery.trim())}`,
      );
      if (!res.found || !res.order) {
        setSearchError(`Order "${orderQuery}" not found`);
      } else {
        navigate(`/orders/${res.order.id}/manual`);
      }
    } catch (e: any) {
      setSearchError(e.message ?? 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [orderQuery, request, navigate]);

  // Send a document without an order
  const [sendOptions, setSendOptions] = useState<SendOption[] | null>(null);
  const [sendTemplateId, setSendTemplateId] = useState('');
  const [sendName, setSendName] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendNotice, setSendNotice] = useState<string | null>(null);

  useEffect(() => {
    request<{ templates: SendOption[] }>('/sessions/send-options')
      .then((res) => {
        setSendOptions(res.templates);
        const first = res.templates.find((t) => t.available);
        if (first) setSendTemplateId(first.id);
      })
      .catch(() => setSendOptions([]));
  }, [request]);

  const latestLoad = useRef(0);
  const load = useCallback(async (status: string, q: string, pageIndex: number) => {
    const id = ++latestLoad.current;
    setLoading(true);
    setError(null);
    try {
      // Ask for one extra row to know whether a next page exists.
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE + 1),
        offset: String(pageIndex * PAGE_SIZE),
      });
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const data = await request<SigningSession[]>(`/sessions?${params}`);
      if (id !== latestLoad.current) return; // a newer search or page replaced this one
      setHasNext(data.length > PAGE_SIZE);
      setSessions(data.slice(0, PAGE_SIZE));
    } catch (e: any) {
      if (id === latestLoad.current) setError(e.message);
    } finally {
      if (id === latestLoad.current) setLoading(false);
    }
  }, [request]);

  useEffect(() => { load(statusFilter, search, page); }, [load, statusFilter, search, page]);

  const sendTemplate = sendOptions?.find((t) => t.id === sendTemplateId);
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sendEmail.trim());

  const handleSendDocument = useCallback(async () => {
    if (!sendTemplate || !sendName.trim() || !emailLooksValid) return;
    setSending(true);
    setSendError(null);
    setSendNotice(null);
    try {
      const res = await request<{ signerName: string; signerEmail: string }>('/sessions/send', {
        method: 'POST',
        body: JSON.stringify({ templateId: sendTemplate.id, signerName: sendName.trim(), signerEmail: sendEmail.trim() }),
      });
      setSendNotice(`“${sendTemplate.name}” was emailed to ${res.signerName} (${res.signerEmail}).`);
      setSendName('');
      setSendEmail('');
      setPage(0);
      load(statusFilter, search, 0);
    } catch (e: any) {
      setSendError(e.message ?? 'Sending failed');
    } finally {
      setSending(false);
    }
  }, [sendTemplate, sendName, sendEmail, emailLooksValid, request, load, statusFilter, search]);

  const filterOptions = [
    { label: 'All statuses', value: '' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Viewed', value: 'VIEWED' },
    { label: 'Signed', value: 'SIGNED' },
    { label: 'Declined', value: 'DECLINED' },
    { label: 'Expired', value: 'EXPIRED' },
  ];

  const rowMarkup = sessions.map((session, index) => (
    <IndexTable.Row
      id={session.id}
      key={session.id}
      position={index}
      onClick={() => navigate(`/sessions/${session.id}`)}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold" as="span">
          {session.signerName}
        </Text>
        <Text variant="bodySm" tone="subdued" as="p">{session.signerEmail}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {session.template?.name ?? '—'}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {(session.recipients?.length ?? 0) > 1 && (session.status === 'PENDING' || session.status === 'VIEWED') ? (
          // Multi-signer document still collecting signatures
          <Badge tone="info">
            {`Signed ${session.recipients!.filter((r) => r.status === 'SIGNED').length} of ${session.recipients!.length}`}
          </Badge>
        ) : (
          <Badge tone={statusTone(session.status)}>
            {session.status.charAt(0) + session.status.slice(1).toLowerCase()}
          </Badge>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {session.shopifyOrderNumber
          ? <Text variant="bodySm" as="span">#{session.shopifyOrderNumber}</Text>
          : <Text variant="bodySm" tone="subdued" as="span">Sent directly</Text>}
      </IndexTable.Cell>
      <IndexTable.Cell>{fmt(session.createdAt)}</IndexTable.Cell>
      <IndexTable.Cell>{fmt(session.signedAt)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Signing Sessions">
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

        {/* Manual send: look up order by number */}
        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">Send Signing Request (Manual)</Text>
            <Text as="p" tone="subdued">Enter an order number to send a signing request to the customer.</Text>
            <InlineStack gap="200" blockAlign="end">
              <div
                style={{ flexGrow: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleManualSearch(); }}
              >
                <TextField
                  label="Order number"
                  labelHidden
                  placeholder="e.g. 1044"
                  value={orderQuery}
                  onChange={setOrderQuery}
                  autoComplete="off"
                  error={searchError ?? undefined}
                />
              </div>
              <Button
                onClick={handleManualSearch}
                loading={searching}
                disabled={!orderQuery.trim() || searching}
              >
                Find Order
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* Send without an order: any active template to a name + email */}
        {sendOptions && (
          <Card>
            <BlockStack gap="300">
              <BlockStack gap="100">
                <Text variant="headingMd" as="h2">Send a document</Text>
                <Text as="p" tone="subdued">
                  Send a template to anyone by email — no Shopify order needed (for example clients who contact you by phone or email).
                </Text>
              </BlockStack>
              {sendNotice && <Banner tone="success" onDismiss={() => setSendNotice(null)}>{sendNotice}</Banner>}
              {sendError && <Banner tone="critical" onDismiss={() => setSendError(null)}>{sendError}</Banner>}
              {sendOptions.length === 0 ? (
                <Text as="p" tone="subdued">
                  You have no active templates yet. Activate a template first — a template set to send <strong>Manual</strong> doesn't need any linked products.
                </Text>
              ) : (
                <>
                  <Select
                    label="Document"
                    options={sendOptions.map((t) => ({
                      label: t.available ? t.name : `${t.name} (several signers — needs Plus or Business)`,
                      value: t.id,
                      disabled: !t.available,
                    }))}
                    value={sendTemplateId}
                    onChange={setSendTemplateId}
                  />
                  <InlineStack gap="300" wrap>
                    <div style={{ flex: '1 1 220px' }}>
                      <TextField label="Name" value={sendName} onChange={setSendName} autoComplete="off" maxLength={120} />
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                      <TextField
                        label="Email"
                        type="email"
                        value={sendEmail}
                        onChange={setSendEmail}
                        autoComplete="off"
                        error={sendEmail.trim() && !emailLooksValid ? 'Enter a valid email address' : undefined}
                      />
                    </div>
                  </InlineStack>
                  {sendTemplate && sendTemplate.signers.length > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {`Signs in this order: 1. ${sendName.trim() || 'this person'} → `}
                      {sendTemplate.signers.map((s, i) => `${i + 2}. ${s.name} (${s.role})`).join(' → ')}
                      {`. Uses ${sendTemplate.signers.length + 1} signing requests.`}
                    </Text>
                  )}
                  <InlineStack>
                    <Button
                      variant="primary"
                      onClick={handleSendDocument}
                      loading={sending}
                      disabled={!sendTemplate?.available || !sendName.trim() || !emailLooksValid || sending}
                    >
                      Send
                    </Button>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        )}

        <InlineStack gap="300" blockAlign="center" wrap>
          <div style={{ flexGrow: 1, minWidth: 220 }}>
            <TextField
              label="Search documents"
              labelHidden
              placeholder="Search by customer name, email or order number"
              value={searchInput}
              onChange={setSearchInput}
              clearButton
              onClearButtonClick={() => setSearchInput('')}
              autoComplete="off"
            />
          </div>
          <Select
            label="Filter by status"
            options={filterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            labelInline
          />
        </InlineStack>

        <IndexTable
          pagination={{
            hasPrevious: page > 0,
            hasNext,
            onPrevious: () => setPage((p) => Math.max(0, p - 1)),
            onNext: () => setPage((p) => p + 1),
            label: `Page ${page + 1}`,
          }}
          emptyState={
            <Text as="p" tone="subdued" alignment="center">
              {search || statusFilter ? 'No documents match your search.' : 'No signing requests sent yet.'}
            </Text>
          }
          resourceName={{ singular: 'session', plural: 'sessions' }}
          itemCount={sessions.length}
          headings={[
            { title: 'Signer' },
            { title: 'Template' },
            { title: 'Status' },
            { title: 'Order' },
            { title: 'Created' },
            { title: 'Signed' },
          ]}
          selectable={false}
          loading={loading}
        >
          {rowMarkup}
        </IndexTable>
      </BlockStack>
    </Page>
  );
}
